import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { faqAPI } from '../../services/faqAPI';

interface FAQState {
  faqs: any[];
  totalFAQs: number;
  currentPage: number;
  totalPages: number;
  isLoading: boolean;
  error: string | null;
}

const initialState: FAQState = {
  faqs: [],
  totalFAQs: 0,
  currentPage: 1,
  totalPages: 0,
  isLoading: false,
  error: null,
};

export const fetchFAQs = createAsyncThunk(
  'faqs/fetchFAQs',
  async (params: { page?: number; limit?: number; search?: string; isActive?: boolean }) => {
    return await faqAPI.getAllFAQs(params);
  }
);

export const createFAQ = createAsyncThunk(
  'faqs/createFAQ',
  async (data: { question: string; answer: string; isActive?: boolean }) => {
    return await faqAPI.createFAQ(data);
  }
);

export const updateFAQ = createAsyncThunk(
  'faqs/updateFAQ',
  async ({ id, data }: { id: string; data: { question?: string; answer?: string; isActive?: boolean } }) => {
    return await faqAPI.updateFAQ(id, data);
  }
);

export const deleteFAQ = createAsyncThunk(
  'faqs/deleteFAQ',
  async (id: string) => {
    await faqAPI.deleteFAQ(id);
    return id;
  }
);

const faqSlice = createSlice({
  name: 'faqs',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFAQs.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchFAQs.fulfilled, (state, action) => {
        state.isLoading = false;
        state.faqs = action.payload.data || [];
        state.totalFAQs = action.payload.total || 0;
        state.currentPage = action.payload.page || 1;
        state.totalPages = action.payload.totalPages || 0;
      })
      .addCase(fetchFAQs.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch FAQs';
      })
      .addCase(createFAQ.fulfilled, (state, action) => {
        state.faqs.unshift(action.payload.data);
      })
      .addCase(updateFAQ.fulfilled, (state, action) => {
        const index = state.faqs.findIndex(faq => faq._id === action.payload.data._id);
        if (index !== -1) {
          state.faqs[index] = action.payload.data;
        }
      })
      .addCase(deleteFAQ.fulfilled, (state, action) => {
        state.faqs = state.faqs.filter(faq => faq._id !== action.payload);
      });
  },
});

export const { clearError } = faqSlice.actions;
export default faqSlice.reducer;